// Three-step wizard orchestrator. Holds the draft, gates Next/Back per
// step validity, POSTs on submit. Routes the user to /sources/:id on
// success.

import { useState } from "react";
import { PickTypeStep } from "./PickTypeStep";
import { ConfigStep, type ConfigDraft } from "./ConfigStep";
import { ConfirmStep } from "./ConfirmStep";
import { createSource } from "./api";
import type {
  CreateSourceBody,
  ReviewPolicy,
  SourceRecord,
  SourceType,
  SyncPolicy,
} from "./types";
import "./sources.css";

type Step = 1 | 2 | 3;

interface Props {
  onCreated?(record: SourceRecord): void;
  onCancel?(): void;
}

export function AddSourceWizard({ onCreated, onCancel }: Props) {
  const [step, setStep] = useState<Step>(1);
  const [type, setType] = useState<SourceType | null>(null);
  const [draft, setDraft] = useState<ConfigDraft>({
    config: {},
    auth_value: "",
    authMethod: "github_app",
  });
  const [syncPolicy, setSyncPolicy] = useState<SyncPolicy>("6h");
  const [reviewPolicy, setReviewPolicy] = useState<ReviewPolicy>("review_required");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const stepValid = isStepValid(step, type, draft);

  const onNext = () => {
    if (step === 1) setStep(2);
    else if (step === 2) setStep(3);
  };

  const onBack = () => {
    if (step === 3) setStep(2);
    else if (step === 2) setStep(1);
  };

  const onSubmit = async () => {
    if (!type) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: CreateSourceBody = {
        type,
        config: draft.config,
        auth_value: draft.auth_value,
        sync_policy: syncPolicy,
        review_policy: reviewPolicy,
      };
      const record = await createSource(body);
      onCreated?.(record);
    } catch (err) {
      setError(err as Error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="maisight-sources-wizard" aria-label="Add a source">
      <header className="maisight-sources-wizard-header">
        <h1>Add a source</h1>
        <ol className="maisight-sources-steps" data-step={step}>
          <li className={step === 1 ? "is-active" : ""}>1. Type</li>
          <li className={step === 2 ? "is-active" : ""}>2. Configure</li>
          <li className={step === 3 ? "is-active" : ""}>3. Confirm</li>
        </ol>
      </header>
      <div className="maisight-sources-wizard-body">
        {step === 1 ? <PickTypeStep value={type} onChange={setType} /> : null}
        {step === 2 && type ? (
          <ConfigStep type={type} draft={draft} onChange={setDraft} />
        ) : null}
        {step === 3 && type ? (
          <ConfirmStep
            type={type}
            config={draft.config}
            syncPolicy={syncPolicy}
            reviewPolicy={reviewPolicy}
            onSyncPolicy={setSyncPolicy}
            onReviewPolicy={setReviewPolicy}
          />
        ) : null}
      </div>
      <footer className="maisight-sources-wizard-footer">
        {onCancel ? (
          <button type="button" className="maisight-sources-btn maisight-sources-btn--ghost" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        {step > 1 ? (
          <button
            type="button"
            className="maisight-sources-btn maisight-sources-btn--ghost"
            onClick={onBack}
          >
            Back
          </button>
        ) : null}
        {step < 3 ? (
          <button
            type="button"
            className="maisight-sources-btn maisight-sources-btn--primary"
            disabled={!stepValid}
            onClick={onNext}
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            className="maisight-sources-btn maisight-sources-btn--primary"
            disabled={!stepValid || submitting}
            onClick={onSubmit}
            data-testid="submit-source"
          >
            {submitting ? "Connecting…" : "Connect"}
          </button>
        )}
        {error ? (
          <span className="maisight-sources-err" role="alert">{error.message}</span>
        ) : null}
      </footer>
    </div>
  );
}

const URL_RE = /^https?:\/\/[^\s]+$/i;

function isStepValid(step: Step, type: SourceType | null, draft: ConfigDraft): boolean {
  if (step === 1) return type !== null;
  if (step === 2 && type === "openapi") {
    return URL_RE.test(String(draft.config.url ?? ""));
  }
  if (step === 2 && type === "git") {
    const hasRepo = String(draft.config.repo ?? "").length > 0;
    const hasAuth = (draft.authMethod ?? "github_app") === "github_app"
      ? Boolean(draft.config.app_installation_id)
      : draft.auth_value.length > 0;
    return hasRepo && hasAuth;
  }
  return true;
}
